import { Field, Input, Label } from '@headlessui/react'
import { type IconProps, WarningOctagon } from '@phosphor-icons/react'
import type {
  ButtonHTMLAttributes,
  ComponentType,
  InputHTMLAttributes,
  ReactNode,
  Ref,
} from 'react'
import { forwardRef } from 'react'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'

import { uiTestId } from '../../test-ids'

export type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> &
  Pick<VariantProps<typeof textFieldVariants>, 'shrinkLabel' | 'blank' | 'truncate'> & {
    'label'?: string
    'unit'?: string
    'IconStart'?: ComponentType<IconProps>
    'IconEnd'?: ComponentType<IconProps>
    'Action'?: ComponentType<ButtonHTMLAttributes<HTMLButtonElement>>
    'flag'?: ReactNode
    'info'?: ReactNode
    'error'?: ReactNode
    'prefix'?: ReactNode
    'suffix'?: ReactNode
    'className'?: string
    'noDynamicPadding'?: boolean
    'data-testid'?: string
    'rootTestId'?: string
  }

export const textFieldVariants = tv({
  slots: {
    base: 'border border-primary/30 text-primary focus-within:border-primary relative flex rounded-lg focus-within:outline-hidden focus-within:ring-0 bg-white',
    flag: 'flag mb-1 ml-3 self-end overflow-hidden',
    iconError: 'text-error grid w-12 place-content-center p-3 *:size-6',
    iconStart: 'grid w-12 place-content-center p-3 *:size-6',
    iconEnd: 'grid w-12 place-content-center p-3 *:size-6',
    action:
      'border-l-primary/30 hover:bg-hover-light active:bg-active-light grid w-12 place-content-center border-l p-3 *:size-6 hover:cursor-pointer',
    input:
      'input peer w-full appearance-none rounded-lg border-none p-3 bg-transparent focus:pb-1 focus:pt-5 focus:outline-hidden focus:ring-0 [&:not(:placeholder-shown)]:pb-1 [&:not(:placeholder-shown)]:pt-5',
    label:
      'label font-base-label text-secondary-text pointer-events-none absolute left-3 top-3 z-10 w-11/12 origin-left -translate-y-2.5 scale-75 truncate transition-transform duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-2.5 peer-focus:scale-75',
    unit: 'label font-base-label text-secondary-text pointer-events-none absolute right-3 top-3 z-10 origin-left translate-y-2.5 truncate transition-transform duration-300 peer-placeholder-shown:translate-y-0',
  },
  variants: {
    readOnly: {
      true: {
        input: 'bg-primary/5 border-primary/5 border',
      },
    },
    noDynamicPadding: {
      true: {
        input: 'focus:p-3 [&:not(:placeholder-shown)]:py-3',
      },
    },
    iconStart: {
      true: { input: 'pl-0', label: 'left-0' },
    },
    iconEnd: {
      true: { input: 'pr-0' },
    },
    iconError: {
      true: {
        base: 'border-error focus-within:border-error',
        label: 'peer-[&:not(:placeholder-shown)]:text-error',
      },
    },
    shrinkLabel: { true: { label: '!-translate-y-2.5 !scale-75' } },
    inputSpacing: {
      false: { input: 'px-0', label: 'left-0' },
    },
    flag: {
      true: {
        input:
          'pt-5.5 focus:pt-5.5 [&:not(:placeholder-shown)]:pt-5.5 pb-0.5 focus:pb-0.5 [&:not(:placeholder-shown)]:pb-0.5 [.flag_+_&]:pl-1.5',
      },
    },
    blank: {
      true: {
        base: 'border-transparent focus-within:border-transparent',
      },
    },
    truncate: {
      true: {
        label: 'right-0 truncate',
      },
    },
  },
  compoundVariants: [
    {
      flag: true,
      iconStart: true,
      class: {
        iconStart: '[&_+_*_.flag]:ml-0 [&_+_*_.input]:pl-0 [&_+_*_.label]:left-0',
      },
    },
  ],
  defaultVariants: { inputSpacing: true, noDynamicPadding: false },
})

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    name,
    label,
    unit,
    placeholder,
    className,
    IconStart,
    IconEnd,
    Action,
    flag,
    blank,
    truncate,
    shrinkLabel,
    info,
    error,
    prefix,
    suffix,
    disabled,
    readOnly = false,
    noDynamicPadding,
    rootTestId,
    ...rest
  },
  ref: Ref<HTMLElement>
) {
  const {
    base,
    input,
    label: labelStyles,
    unit: unitStyles,
    iconStart,
    iconEnd,
    action,
    flag: flagStyles,
    iconError,
  } = textFieldVariants({
    iconStart: !!IconStart,
    iconEnd: !!IconEnd,
    iconError: !!error,
    flag: !!flag,
    shrinkLabel,
    blank,
    truncate,
    noDynamicPadding,
    readOnly,
  })

  return (
    <div className={className} data-testid={rootTestId}>
      <Field className={base()}>
        {prefix}
        {IconStart && (
          <span className={iconStart()}>
            <IconStart />
          </span>
        )}

        <div className="relative flex flex-1 text-field-wrapper">
          {flag && <span className={flagStyles()}>{flag}</span>}
          <Input
            ref={ref}
            id={name}
            name={name}
            className={input()}
            placeholder={placeholder ?? ' '}
            disabled={disabled}
            readOnly={readOnly}
            {...rest}
          />
          {label && (
            <Label htmlFor={name} className={labelStyles()}>
              {label}
              {rest.required && '*'}
            </Label>
          )}

          {unit && <span className={unitStyles()}>{unit}</span>}
        </div>
        {error && (
          <span className={iconError()}>
            <WarningOctagon />
          </span>
        )}
        {IconEnd && (
          <span className={iconEnd()}>
            <IconEnd />
          </span>
        )}
        {suffix}
        {Action && <Action disabled={disabled} className={action()} />}
      </Field>
      {info && <p className="pt-2 pl-3 font-base-caption text-secondary-text">{info}</p>}
      {error && (
        <p
          className="pt-2 pl-3 font-base-caption text-error"
          data-testid={uiTestId('TextField.error')}
        >
          {error}
        </p>
      )}
    </div>
  )
})

TextField.displayName = 'TextField'
